import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Rating,
  Chip,
  Button,
  TextField,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  styled,
  Skeleton,
  Alert,
  LinearProgress
} from '@mui/material';
import { 
  Add as AddIcon, 
  PhotoCamera, 
  SmartToy,
  Upgrade,
  CheckCircle,
  Business,
  Person
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useSpCommentPostMutation } from '../../../redux/thunks/sp/spcommentpost';
import { useGenerateTextMutation } from '../../../redux/thunks/ai/generatetext';
import { useGetReviewByIdQuery } from '../../../redux/thunks/unauth/getreviewbyid';
import { useGetUserProfileQuery } from '../../../redux/thunks/users/userprofile';

// Utility function to get current month key
const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}`; // Add 1 since getMonth() is 0-based
};

// AI Quota Constants
const AI_QUOTAS = {
  review_generation: {
    free: process.env.REACT_APP_BUILD_ENV === "prod" ? 50 : 3,
    premium: Infinity // unlimited - for normal users commenting
  },
  comment_responses: {
    free: process.env.REACT_APP_BUILD_ENV === "prod" ? 5 : 2,
    premium: Infinity // unlimited - for business owners responding
  }
};

// Styled components for MUI v5
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  '& .MuiCardContent-root': {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  }
}));

const PhotoUploadButton = styled(IconButton)(({ theme }) => ({
  width: 100,
  height: 100,
  border: '1px dashed',
  borderColor: theme.palette.grey[300],
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const CommentBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2)
}));

// AI Quota Display Component
const AIQuotaDisplay = ({ 
  userAccountType, 
  currentUsage, 
  quotaLimit, 
  isLoading, 
  quotaType,
  isBusinessOwner 
}) => {
  if (isLoading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="textSecondary">
          Loading AI usage...
        </Typography>
      </Box>
    );
  }

  const usagePercentage = quotaLimit === Infinity ? 0 : (currentUsage / quotaLimit) * 100;
  const canUseAI = quotaLimit === Infinity || currentUsage < quotaLimit;

  const getQuotaLabel = () => {
    if (isBusinessOwner) {
      return 'AI Response Generation';
    }
    return 'AI Comment Generation';
  };

  const getQuotaDescription = () => {
    if (isBusinessOwner) {
      return userAccountType === 'premium' 
        ? 'Unlimited AI response generation' 
        : canUseAI 
          ? `${quotaLimit - currentUsage} AI responses remaining this month`
          : 'Monthly AI response limit reached';
    }
    return userAccountType === 'premium' 
      ? 'Unlimited AI comment generation' 
      : canUseAI 
        ? `${quotaLimit - currentUsage} AI comments remaining this month`
        : 'Monthly AI comment limit reached';
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isBusinessOwner ? <Business fontSize="small" /> : <Person fontSize="small" />}
          {getQuotaLabel()}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {quotaLimit === Infinity ? `${currentUsage} (Unlimited)` : `${currentUsage}/${quotaLimit}`}
        </Typography>
      </Box>
      
      {quotaLimit !== Infinity && (
        <Box sx={{ width: '100%', mb: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(usagePercentage, 100)}
            color={canUseAI ? 'primary' : 'error'}
          />
        </Box>
      )}
      
      <Typography 
        variant="caption" 
        color={canUseAI ? 'success.main' : 'error.main'}
      >
        {getQuotaDescription()}
      </Typography>
    </Box>
  );
};

// AI Limit Exceeded Component
const AILimitExceeded = ({ userAccountType, onUpgrade, isBusinessOwner }) => {
  const getMessageText = () => {
    if (isBusinessOwner) {
      return userAccountType === 'free' 
        ? 'You\'ve reached your monthly AI response generation limit. Upgrade to Premium for unlimited AI assistance when responding to reviews!'
        : 'You\'ve reached your monthly AI response generation limit. Please try again next month.';
    }
    return userAccountType === 'free' 
      ? 'You\'ve reached your monthly AI comment generation limit. Upgrade to Premium for unlimited AI assistance!'
      : 'You\'ve reached your monthly AI comment generation limit. Please try again next month.';
  };

  const getPremiumBenefits = () => {
    if (isBusinessOwner) {
      return [
        'Unlimited AI response generation',
        'Advanced business analytics',
        'Priority customer support',
        'Enhanced business features'
      ];
    }
    return [
      'Unlimited AI comment generation',
      'Unlimited AI review generation',
      'Advanced AI features',
      'Priority support'
    ];
  };

  return (
    <Alert 
      severity="warning" 
      sx={{ mb: 2 }}
      action={
        userAccountType === 'free' ? (
          <Button 
            color="inherit" 
            size="small" 
            startIcon={<Upgrade />}
            onClick={onUpgrade}
          >
            Upgrade
          </Button>
        ) : null
      }
    >
      <Typography variant="body2">
        {getMessageText()}
      </Typography>
      
      {userAccountType === 'free' && (
        <Box sx={{ mt: 1 }}>
          {getPremiumBenefits().map((benefit, index) => (
            <Typography key={index} variant="caption" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <CheckCircle sx={{ mr: 0.5, fontSize: 14 }} />
              {benefit}
            </Typography>
          ))}
        </Box>
      )}
    </Alert>
  );
};

const COMMENT_TYPES = [
  { id: 'agree', label: 'Agree', color: 'success' },
  { id: 'disagree', label: 'Disagree', color: 'error' },
  { id: 'question', label: 'Question', color: 'default' }
];

const TONES = [
  { id: 'professional', label: 'Professional' },
  { id: 'casual', label: 'Casual' }
];

const ReviewCommentsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const params = useParams(); // For direct URL access
  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const token = useSelector((state) => state.auth?.token || null);
  const thisUserPhoto = useSelector((state) => state.auth?.user?.profileImageUrl || '');
  const thisUserdisplayName = useSelector((state) => state.auth?.user?.displayName || '');
  const userId = useSelector((state) => state.auth?.userId || null);
  
  // State to hold review data - initialized empty since we might load it from API
  const [reviewData, setReviewData] = useState(null);
  const [businessNameData, setBusinessNameData] = useState('');
  const [directUrlAccess, setDirectUrlAccess] = useState(false);
  
  // Comments and form state
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = authStatus === 'authenticated' && token;
  const [commentType, setCommentType] = useState('');
  const [tone, setTone] = useState('professional');
  const [comment, setComment] = useState('');
  const [keyPoints, setKeyPoints] = useState(['', '']);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // API mutations
  const [postComment, { isLoading: isCommentsLoading }] = useSpCommentPostMutation();
  const [generateText, { isLoading: isGenerateTextLoading }] = useGenerateTextMutation();
  const { data: userData, isError: userProfileError, isLoading: isUserLoading, error } = useGetUserProfileQuery(userId);
  console.log('userData', userData);
  
  // Query to get review details when accessed directly via URL
  const reviewId = params?.reviewId || '';
  console.log('reviewId', reviewId);
  console.log('location.state', location.state);
  const authType = isAuthenticated ? 'token' : 'none';
  const { data: fetchedReview, isLoading: isReviewLoading } = useGetReviewByIdQuery(
    { 
      reviewId, 
      authType,
      token: token || ''
    }, 
    { skip: !reviewId || (location.state && location.state.review) }
  );

  console.log('fetchedReview', fetchedReview);

  // Determine if current user is business owner
  const isBusinessOwner = useMemo(() => {
    if (!userData || !reviewData) return false;
    
    // Check if user owns any businesses that match the review's business
    const userBusinesses = userData.myBusinesses || [];
    return userBusinesses.some(business => business.businessId === reviewData.businessId);
  }, [userData, reviewData]);
  console.log('isBusinessOwner', isBusinessOwner)

  // AI Quota Logic
  const userAccountType = userData?.accountDetails?.type || 'free';
  
  // Use different quotas based on whether user is business owner or regular user
  const quotaType = isBusinessOwner ? 'comment_responses' : 'review_generation';
  const aiQuotaLimit = AI_QUOTAS[quotaType][userAccountType] || AI_QUOTAS[quotaType].free;
  
  // Get current month's AI usage
  const currentMonthKey = getCurrentMonthKey();
  
  // Get appropriate usage data based on user type
  const currentAIUsage = useMemo(() => {
    if (isBusinessOwner) {
      const commentResponsesUsage = userData?.quotaUsage?.ai_comment_responses?.monthly || {};
      return commentResponsesUsage[currentMonthKey] || 0;
    } else {
      const reviewGenerationUsage = userData?.quotaUsage?.ai_review_generation?.monthly || {};
      return reviewGenerationUsage[currentMonthKey] || 0;
    }
  }, [userData, currentMonthKey, isBusinessOwner]);
  
  const canUseAI = aiQuotaLimit === Infinity || currentAIUsage < aiQuotaLimit;

  const handleUpgradeClick = () => {
    navigate('/premium');
  };

  // Determine if accessed directly via URL or from navigation
  useEffect(() => {
    if (location.state && location.state.review) {
      // Came from navigation with state
      setReviewData(location.state.review);
      setBusinessNameData(location.state.businessName || '');
      setComments(location.state.review.comments || []);
      setDirectUrlAccess(false);
      setIsLoading(false);
    } else if (reviewId) {
      // Direct URL access
      setDirectUrlAccess(true);
      if (fetchedReview) {
        console.log("Fetched review data:", fetchedReview);
        setReviewData(fetchedReview);
        setBusinessNameData(fetchedReview.businessName || '');
        setComments(fetchedReview.comments || []);
        setIsLoading(false);
      } else if (isReviewLoading) {
        setIsLoading(true);
      } else if (!isReviewLoading && !fetchedReview) {
        // If review couldn't be fetched, redirect to home
        console.error("Could not fetch review");
        navigate('/');
      }
    } else {
      // Invalid access without review data
      navigate('/');
    }
  }, [location.state, fetchedReview, reviewId, navigate, isReviewLoading]);

  const handleGenerateComment = async () => {
    if (!isAuthenticated) {
      // Save the current path for redirection after sign-in
      navigate('/auth/signin', { 
        state: { 
          link: location.pathname,
          reviewId: reviewData?._id
        } 
      });
      return;
    }

    // Check AI quota before generation
    if (!canUseAI) {
      return;
    }

    setIsGenerating(true);
    let auth = {
      token: token,
      authType: 'token'
    };
    
    const payload = {
      auth,
      businessId: reviewData.businessId,
      keywords: keyPoints.filter(kw => kw !== ''),
      type: tone,
      tone: tone,
      generationType: 'comment',
      review: reviewData.review,
      comments: reviewData.comments,
      isBusinessOwner: isBusinessOwner
    };

    try {
      const generatedComment = await generateText({payload});
      if (generatedComment.data) {
        setComment(generatedComment.data.text);
      }
    } catch (error) {
      console.error('Error generating comment:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddComment = async () => {
    if (!isAuthenticated) {
      // Save the current path for redirection after sign-in
      navigate('/auth/signin', { 
        state: { 
          link: location.pathname,
          reviewId: reviewData?._id
        } 
      });
      return;
    }

    if (comment.trim() === '') {
      return;
    }

    let auth = {
      token: token,
      authType: 'token'
    };

    let latestComment = {
      comment: comment,
      reviewId: reviewData._id
    };

    const payload = {
      auth,
      review: latestComment,
      spId: reviewData.spId
    };

    try {
      const response = await postComment({ payload });
      // Add the new comment to the local state
      const updatedComments = [
        ...comments, 
        { 
          displayName: thisUserdisplayName, 
          comment: comment, 
          created: new Date(), 
          photo: thisUserPhoto 
        }
      ];
      setComments(updatedComments);
      setComment('');
    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };

  // Loading skeleton components
  const LoadingReview = () => (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Skeleton variant="circular" width={56} height={56} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="text" width={120} height={24} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" height={100} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="rectangular" width={120} height={80} />
        <Skeleton variant="rectangular" width={120} height={80} />
        <Skeleton variant="rectangular" width={120} height={80} />
      </Box>
      <Box>
        <Skeleton variant="text" width={200} height={32} />
        {[1, 2].map((_, index) => (
          <Box key={index} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={120} />
                <Skeleton variant="text" width={80} />
              </Box>
            </Box>
            <Skeleton variant="rectangular" height={60} />
          </Box>
        ))}
      </Box>
    </Stack>
  );

  const LoadingComment = () => (
    <Box sx={{ width: '100%' }}>
      <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
      <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
      <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
    </Box>
  );

  const LoadingSuggestion = () => (
    <Card variant="outlined">
      <CardContent>
        <Skeleton variant="text" sx={{ mb: 1 }} />
        <Skeleton variant="text" width="80%" sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" width={80} height={32} />
      </CardContent>
    </Card>
  );

  if (isLoading || isReviewLoading || !reviewData) {
    return (
      <Box sx={{ maxWidth: theme.breakpoints.values.lg, margin: '0 auto', p: 2 }}>
        <LoadingReview />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: theme.breakpoints.values.lg, margin: '0 auto', p: 2 }}>
      {!isAuthenticated && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => navigate('/auth/signin', { 
                state: { 
                  link: location.pathname,
                  reviewId: reviewData?._id
                } 
              })}
            >
              Sign In
            </Button>
          }
        >
          Sign in to post comments and interact with the review.
        </Alert>
      )}

      {/* AI Limit Exceeded Alert - Full Width */}
      {isAuthenticated && !canUseAI && (
        <AILimitExceeded 
          userAccountType={userAccountType}
          onUpgrade={handleUpgradeClick}
          isBusinessOwner={isBusinessOwner}
        />
      )}
      
      <Grid container spacing={2}>
        {/* Review Content */}
        <Grid item xs={12} md={5}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 56, height: 56 }}>
                  {reviewData.userDisplayName[0] || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6">{reviewData.userDisplayName || 'Anonymous User'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {reviewData.created}
                  </Typography>
                  <Rating value={reviewData.rating} readOnly />
                </Box>
              </Box>

              <Typography variant="body1" paragraph>
                {reviewData.review}
              </Typography>

              {reviewData.photos && reviewData.photos.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Photos ({reviewData.photos.length})
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {reviewData.photos.map((photo, index) => (
                      <Box
                        key={index}
                        component="img"
                        src={photo}
                        alt={`Review ${index + 1}`}
                        sx={{
                          width: 120,
                          height: 80,
                          borderRadius: 1,
                          objectFit: 'cover'
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Box>
                <Typography variant="h6" gutterBottom>
                  Previous Comments ({comments.length})
                </Typography>
                <Stack spacing={2}>
                  {comments.map((comment, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Avatar
                          src={comment.photo}
                          sx={{ width: 32, height: 32 }}
                        >
                          {comment.displayName?.[0] || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {comment.displayName || 'Anonymous'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {comment.created.toString()}
                          </Typography>
                        </Box>
                      </Box>
                      <CommentBox>
                        <Typography variant="body2">
                          {comment.comment}
                        </Typography>
                      </CommentBox>
                    </Box>
                  ))}
                  {comments.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No comments yet. Be the first to comment!
                    </Typography>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Comment Writing */}
        <Grid item xs={12} md={4}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {isBusinessOwner ? 'Respond to Review' : 'Write a Comment'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {isBusinessOwner 
                  ? 'Respond to this review as the business owner'
                  : 'Share your thoughts about this review'
                }
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Comment Type
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {COMMENT_TYPES.map(type => (
                    <Chip
                      key={type.id}
                      label={type.label}
                      color={type.color}
                      onClick={() => setCommentType(type.id)}
                      variant={commentType === type.id ? 'filled' : 'outlined'}
                      sx={{ m: 0.5 }}
                      disabled={!isAuthenticated}
                    />
                  ))}
                </Stack>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Your {isBusinessOwner ? 'Response' : 'Comment'}
                </Typography>
                {isGenerating ? (
                  <LoadingComment />
                ) : (
                  <TextField
                    multiline
                    rows={8}
                    fullWidth
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={isAuthenticated 
                      ? (isBusinessOwner ? "Write your response here..." : "Write your comment here...")
                      : "Sign in to comment"
                    }
                    variant="outlined"
                    disabled={!isAuthenticated}
                  />
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Attachments (Optional)
                </Typography>
                <PhotoUploadButton component="label" disabled={!isAuthenticated}>
                  <input hidden accept="image/*" type="file" disabled={!isAuthenticated} />
                  <AddIcon />
                </PhotoUploadButton>
              </Box>

              <Stack spacing={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddComment} 
                  disabled={isCommentsLoading || !isAuthenticated || comment.trim() === ''}
                  fullWidth
                >
                  {isAuthenticated 
                    ? (isBusinessOwner ? 'Post Response' : 'Post Comment')
                    : 'Sign In to Comment'
                  }
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* AI Assistant */}
        <Grid item xs={12} md={3}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToy color="primary" />
                AI Assistant
              </Typography>

              {/* Show business owner indicator */}
              {isBusinessOwner && (
                <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                  <Typography variant="caption">
                    You're responding as the business owner
                  </Typography>
                </Alert>
              )}

              {/* AI Quota Display */}
              {isAuthenticated && (
                <AIQuotaDisplay 
                  userAccountType={userAccountType}
                  currentUsage={currentAIUsage}
                  quotaLimit={aiQuotaLimit}
                  isLoading={isUserLoading}
                  quotaType={quotaType}
                  isBusinessOwner={isBusinessOwner}
                />
              )}

              {/* Compact limit indicator for narrow column */}
              {isAuthenticated && !canUseAI && (
                <Alert severity="warning" sx={{ mb: 2, py: 1 }}>
                  <Typography variant="caption">
                    {isBusinessOwner ? 'Response limit reached' : 'Comment limit reached'}
                  </Typography>
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Select Tone
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {TONES.map(t => (
                    <Chip
                      key={t.id}
                      label={t.label}
                      onClick={() => setTone(t.id)}
                      variant={tone === t.id ? 'filled' : 'outlined'}
                      sx={{ m: 0.5 }}
                      disabled={!isAuthenticated || !canUseAI}
                    />
                  ))}
                </Stack>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Key Points
                </Typography>
                <Stack spacing={1}>
                  {keyPoints.map((point, index) => (
                    <TextField
                      key={index}
                      size="small"
                      value={point}
                      onChange={(e) => {
                        const newPoints = [...keyPoints];
                        newPoints[index] = e.target.value;
                        setKeyPoints(newPoints);
                      }}
                      placeholder={`Key point ${index + 1}`}
                      variant="outlined"
                      disabled={!isAuthenticated || !canUseAI}
                    />
                  ))}
                </Stack>
              </Box>

              <Button
                variant="contained"
                fullWidth
                onClick={handleGenerateComment}
                disabled={isGenerating || !isAuthenticated || !canUseAI}
                sx={{ mb: 3 }}
                startIcon={<SmartToy />}
              >
                {isGenerating 
                  ? 'Generating...' 
                  : isAuthenticated 
                    ? (canUseAI 
                        ? (isBusinessOwner ? 'Generate Response' : 'Generate Comment')
                        : 'Quota Exceeded'
                      )
                    : 'Sign In to Use AI'
                }
              </Button>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Suggestions
                </Typography>
                <Stack spacing={2}>
                  {isGenerating ? (
                    <>
                      <LoadingSuggestion />
                      <LoadingSuggestion />
                    </>
                  ) : (
                    ['I completely agree with your experience. The professional service and cleanliness you mentioned are exactly what makes this place stand out.',
                     'Thank you for sharing. I\'d like to know more about their cleaning protocols...'].map((suggestion, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {suggestion}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setComment(suggestion)}
                            disabled={!isAuthenticated}
                          >
                            Use This
                          </Button>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Stack>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReviewCommentsPage;